import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Mail, Phone, MapPin, Send, Info } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/i18n/translations";
import { WHATSAPP_DISPLAY } from "@/lib/whatsapp";
import { getHubspotUtk, loadHubSpotTracking } from "@/lib/hubspot";
import { GROUP_SIZE_EVENT } from "@/components/GroupSizeSelector";
import { trackEvent, AnalyticsEvents } from "@/utils/analytics";

const Contact = () => {
  const { toast } = useToast();
  const { language } = useLanguage();
  const t = translations[language].contact;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: ""
  });

  useEffect(() => {
    loadHubSpotTracking();
  }, []);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<string>).detail;
      if (!detail) return;
      setFormData((prev) => ({
        ...prev,
        message: prev.message.trim() ? prev.message : detail,
      }));
    };
    window.addEventListener(GROUP_SIZE_EVENT, handler);
    return () => window.removeEventListener(GROUP_SIZE_EVENT, handler);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Trim all form values
      const trimmedData = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        company: formData.company.trim(),
        message: formData.message.trim(),
        pageUri: typeof window !== 'undefined' ? window.location.href : undefined,
        pageName: typeof document !== 'undefined' ? document.title : undefined,
        hutk: getHubspotUtk(),
      };

      console.log('Submitting contact form to HubSpot...');
      
      const { data, error } = await supabase.functions.invoke('submit-contact-form', {
        body: trimmedData
      });


      if (error) {
        console.error('Error submitting form:', error);
        toast({
          title: t.error,
          description: t.errorDescription,
          variant: "destructive"
        });
        return;
      }

      console.log('Form submitted successfully:', data);

      // Server-generated transaction id — appended to the /bedankt redirect so
      // the Google Ads conversion event carries a stable id for deduplication.
      const tid = (data as { tid?: string } | null)?.tid;
      const bedanktUrl = tid ? `/bedankt?tid=${encodeURIComponent(tid)}` : '/bedankt';

      trackEvent(AnalyticsEvents.CONTACT_SUBMIT, {
        form: 'contact',
        language,
        ...(tid ? { transaction_id: tid } : {}),
      });

      // Redirect to the dedicated thank-you page for conversion tracking
      window.location.href = bedanktUrl;
      return;
    } catch (error) {
      console.error('Unexpected error:', error);
      toast({
        title: t.error,
        description: t.unexpectedError,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-14 sm:py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-10 sm:mb-12 md:mb-16 animate-fade-in">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4 px-4">
            {t.title} <span className="text-primary">{t.titleHighlight}</span>
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto px-4">
            {t.description}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
          {/* Contact Info Cards */}
          <div className="space-y-4 sm:space-y-6 animate-fade-in">
            <Card className="bg-transparent border-border hover:border-primary/50 transition-colors duration-300">
              <CardHeader>
                <Phone className="w-5 h-5 text-primary mb-3" />
                <CardTitle>{language === 'nl' ? 'WhatsApp / Bel ons' : 'WhatsApp / Call us'}</CardTitle>
                <CardDescription>
                  {WHATSAPP_DISPLAY}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="bg-transparent border-border hover:border-primary/50 transition-colors duration-300">
              <CardHeader>
                <Mail className="w-5 h-5 text-primary mb-3" />
                <CardTitle>{t.emailUs}</CardTitle>
                <CardDescription>
                  info@playfieldvr.nl
                </CardDescription>
              </CardHeader>
            </Card>


            <Card className="bg-transparent border-border hover:border-primary/50 transition-colors duration-300">
              <CardHeader>
                <MapPin className="w-5 h-5 text-primary mb-3" />
                <CardTitle>{t.location}</CardTitle>
                <CardDescription>
                  {t.locationText}
                </CardDescription>
              </CardHeader>
            </Card>
          </div>

          {/* Contact Form */}
          <div id="contact-form" className="lg:col-span-2 animate-fade-in scroll-mt-24" style={{ animationDelay: "0.2s" }}>
            <Card className="bg-transparent border-border">
              <CardHeader>
                <CardTitle className="text-xl sm:text-2xl">{t.formTitle}</CardTitle>
                <CardDescription className="text-sm sm:text-base">
                  {t.formDescription}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 mb-5">
                  {(language === 'nl'
                    ? ["6 tot 60+ personen", "Op jouw locatie", "Reactie binnen 24 uur"]
                    : ["6 to 60+ people", "At your location", "Reply within 24 hours"]
                  ).map((item) => (
                    <span key={item} className="text-xs sm:text-sm px-3 py-1.5 rounded-full border border-border text-muted-foreground">
                      {item}
                    </span>
                  ))}
                </div>
                <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">{t.form.name} *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="bg-background/50 border-border focus:border-primary"
                        placeholder={t.form.namePlaceholder}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">{t.form.email} *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="bg-background/50 border-border focus:border-primary"
                        placeholder={t.form.emailPlaceholder}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">{t.form.phone} *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="bg-background/50 border-border focus:border-primary"
                        placeholder={t.form.phonePlaceholder}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="company">{t.form.company}</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="bg-background/50 border-border focus:border-primary"
                        placeholder={t.form.companyPlaceholder}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">{t.form.message} *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={6}
                      className="bg-background/50 border-border focus:border-primary resize-none"
                      placeholder={t.form.messagePlaceholder}
                    />
                  </div>

                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <span className="font-semibold text-primary">{t.priceAnchor}</span>
                    <TooltipProvider delayDuration={100}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex items-center justify-center text-muted-foreground hover:text-primary transition-colors"
                            aria-label={t.priceAnchorNote}
                          >
                            <Info className="w-4 h-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs text-center">
                          <p>{t.priceAnchorNote}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={isSubmitting}
                    className="w-full bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? t.form.submitting : t.form.submit}
                    <Send className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
